import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { useAuth } from "./AuthContext";

export type DocumentCategory = "Invoice" | "Purchase Bill" | "Expense" | "Payment" | "GST Filing" | "Other";

export interface DocumentFile {
  id: string;
  name: string;
  size: number;
  type: string; // mime type
  category: DocumentCategory;
  linkedEntityId?: string;
  linkedEntityRef?: string;
  uploadedBy: string;
  uploadedByName: string;
  uploadedAt: string;
  tags: string[];
  dataUrl: string; // base64 for local demo
  thumbnailUrl?: string;
}

interface DocumentContextType {
  documents: DocumentFile[];
  uploadDocument: (file: File, category: DocumentCategory, linkedEntityId?: string, linkedEntityRef?: string, tags?: string[]) => Promise<DocumentFile>;
  deleteDocument: (id: string) => void;
  getDocumentsByEntity: (entityId: string) => DocumentFile[];
  getDocumentsByCategory: (category: DocumentCategory) => DocumentFile[];
}

const DocumentContext = createContext<DocumentContextType | null>(null);

const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const MAX_SIZE = 10 * 1024 * 1024; // 10MB

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function DocumentProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<DocumentFile[]>([]);

  const uploadDocument = useCallback(async (
    file: File,
    category: DocumentCategory,
    linkedEntityId?: string,
    linkedEntityRef?: string,
    tags: string[] = []
  ): Promise<DocumentFile> => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error(`File type "${file.type}" is not supported. Allowed: PDF, JPG, PNG, DOCX`);
    }
    if (file.size > MAX_SIZE) {
      throw new Error("File size exceeds 10MB limit");
    }

    const dataUrl = await readFileAsDataUrl(file);

    const doc: DocumentFile = {
      id: `doc-${crypto.randomUUID().slice(0, 8)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      category,
      linkedEntityId,
      linkedEntityRef,
      uploadedBy: user?.id || "unknown",
      uploadedByName: user?.name || "Unknown",
      uploadedAt: new Date().toISOString(),
      tags,
      dataUrl,
    };

    setDocuments((prev) => [doc, ...prev]);
    return doc;
  }, [user]);

  const deleteDocument = useCallback((id: string) => {
    setDocuments((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const getDocumentsByEntity = useCallback(
    (entityId: string) => documents.filter((d) => d.linkedEntityId === entityId),
    [documents]
  );

  const getDocumentsByCategory = useCallback(
    (category: DocumentCategory) => documents.filter((d) => d.category === category),
    [documents]
  );

  return (
    <DocumentContext.Provider value={{ documents, uploadDocument, deleteDocument, getDocumentsByEntity, getDocumentsByCategory }}>
      {children}
    </DocumentContext.Provider>
  );
}

export function useDocuments() {
  const ctx = useContext(DocumentContext);
  if (!ctx) throw new Error("useDocuments must be used within DocumentProvider");
  return ctx;
}
